import { PERMISSIONS } from "../constants/permissions";
import { expandStoredIsfPermissionKeys } from "../constants/isfPermissionConsolidation";
import { guidOrNull } from "./guidUtils";

export const parsePermissions = (permissionString = "") =>
  String(permissionString || "")
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);

export const SYSTEM_ADMINISTRATOR_ROLE = "System Administrator";
/** AQ platform operator — manages customer organisations (not a customer System Administrator). */
export const SUPER_ADMIN_ROLE = "Super Admin";

export const isSystemAdministratorRole = (roleName) =>
  String(roleName || "")
    .trim()
    .toLowerCase() === SYSTEM_ADMINISTRATOR_ROLE.toLowerCase();

export const isSuperAdminRole = (roleName) =>
  String(roleName || "")
    .trim()
    .toLowerCase() === SUPER_ADMIN_ROLE.toLowerCase();

/** Roles that never appear on Role Management (platform / external reserved). */
export function isHiddenFromRoleManagement(roleName) {
  const n = String(roleName || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
  return (
    n === "super admin" ||
    n === "system administrator" ||
    n === "sponsor" ||
    n === "cro" ||
    n === "sponsor/cro" ||
    n === "sponsor cro"
  );
}

/** System Administrator (customer org) or Super Admin (AQ platform). */
export const isPlatformOrSystemAdminRole = (roleName) =>
  isSystemAdministratorRole(roleName) || isSuperAdminRole(roleName);

/** Resolve role from session (API may expose Role or Roles). */
export const getSessionRoleName = (userData) =>
  String(userData?.Role || userData?.Roles || "").trim();

function findPlatformSuperAdminOrgLink(userData) {
  const links = userData?.OtherOrgLink;
  if (!Array.isArray(links)) return null;
  return (
    links.find((item) => isSuperAdminRole(item?.Roles || item?.Role)) || null
  );
}

/** True when the session belongs to an AQ platform Super Admin (including after org switch). */
export const isSessionSuperAdmin = (userData) =>
  userData?.IsPlatformSuperAdmin === true ||
  isSuperAdminRole(getSessionRoleName(userData)) ||
  Boolean(findPlatformSuperAdminOrgLink(userData));

/**
 * Preserve AQ platform Super Admin identity across organisation switches.
 * Customer org sessions use a different StaffId/Role but must keep platform access.
 * Fresh login must pass priorSession=null (and clear the browser session first)
 * so another user's Super Admin flag cannot leak.
 */
export const stampPlatformSuperAdminSession = (userData, priorSession = null) => {
  if (!userData || typeof userData !== "object") return userData;

  const next = { ...userData };
  const role = getSessionRoleName(next);

  if (isSuperAdminRole(role)) {
    next.IsPlatformSuperAdmin = true;
    next.PlatformStaffId = next.PlatformStaffId || next.StaffId || null;
    return next;
  }

  const prior =
    priorSession?.IsPlatformSuperAdmin === true ? priorSession : null;
  const linkedSuperAdmin = findPlatformSuperAdminOrgLink(next);

  if (prior?.IsPlatformSuperAdmin === true) {
    next.IsPlatformSuperAdmin = true;
    next.PlatformStaffId =
      prior.PlatformStaffId || prior.StaffId || linkedSuperAdmin?.StaffId || null;
    return next;
  }

  if (linkedSuperAdmin) {
    next.IsPlatformSuperAdmin = true;
    next.PlatformStaffId = linkedSuperAdmin.StaffId || next.StaffId || null;
    return next;
  }

  delete next.IsPlatformSuperAdmin;
  delete next.PlatformStaffId;
  return next;
};

/** StaffId for AQ Super Admin API calls — stable even after switching customer org. */
export const resolvePlatformSuperAdminStaffId = (userData) => {
  if (!userData) return null;
  if (userData.PlatformStaffId) return userData.PlatformStaffId;
  if (isSessionSuperAdmin(userData)) return userData.StaffId || null;
  return null;
};

/** Organisation Setup — old web Admin menu used AdminRole permission. */
export const canAccessOrganizationSetup = (userData) => {
  const role = getSessionRoleName(userData);
  if (isPlatformOrSystemAdminRole(role)) return true;
  const perms = getEffectivePermissionKeys(role, userData?.Permission);
  return perms.includes("AdminRole") || perms.includes("ADMIN_ROLE");
};

/** Role + stored permission string → effective keys (System Administrator / Super Admin = all). */
export const getEffectivePermissionKeys = (roleName, permissionString = "") => {
  if (isPlatformOrSystemAdminRole(roleName)) {
    return getAllPermissionKeys();
  }
  return parsePermissions(permissionString);
};

/** Session user → { isSystemAdmin, isSuperAdmin, permissions }. */
export const resolveSessionPermissions = (userData) => {
  const role = getSessionRoleName(userData);
  const isSuperAdmin = isSuperAdminRole(role);
  const isSystemAdmin =
    isSuperAdmin ||
    userData?.Role === SYSTEM_ADMINISTRATOR_ROLE ||
    userData?.Roles === SYSTEM_ADMINISTRATOR_ROLE ||
    isSystemAdministratorRole(role);

  return {
    isSystemAdmin,
    isSuperAdmin,
    permissions: isSystemAdmin
      ? getAllPermissionKeys()
      : expandStoredIsfPermissionKeys(parsePermissions(userData?.Permission), {
          stripLegacy: false,
        }),
  };
};

/** True when System Administrator / Super Admin or the permission key is assigned. */
export const userHasSessionPermission = (userData, permissionKey) => {
  const { isSystemAdmin, permissions } = resolveSessionPermissions(userData);
  return isSystemAdmin || permissions.includes(permissionKey);
};

/** Resolve staff id for API calls; never return organization id by mistake. */
export const resolveSessionStaffId = (userData) => {
  const organizationId = guidOrNull(userData?.OrganizationId);
  const candidates = [
    userData?.StaffId,
    userData?.staffId,
    userData?.StaffRecordId,
    userData?.staffRecordId,
    userData?.LoggedInUserId,
    userData?.loggedInUserId,
  ];

  for (const candidate of candidates) {
    const staffId = guidOrNull(candidate);
    if (!staffId) continue;
    if (organizationId && staffId === organizationId) continue;
    return staffId;
  }

  return null;
};

export const hasPermission = (userPermissions, permissionKey) => {
  return userPermissions.includes(permissionKey);
};

export const hasAnyModulePermission = (userPerms, modulePermissions = []) => {
  return modulePermissions.some((p) => userPerms.has(p.key));
};

export const getAllPermissionKeys = () => {
  return Object.values(PERMISSIONS)
    .flat()
    .map((p) => p.key);
};
